import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Alert,
  Badge,
  ListGroup,
  Image,
  Spinner
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaUserTie,
  FaCalendarCheck,
  FaHistory,
  FaChartBar
} from "react-icons/fa";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import Chart from "react-apexcharts";
import * as moment from "moment";
import { dashboardService } from "../../services/api";

const ClientDashboard = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [activePackage, setActivePackage] = useState(null);
  const [incomingTasks, setIncomingTasks] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState({
    weeks: [],
    boost_live: [],
    boost_post: [],
    consult: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [pagesResponse, packageResponse, tasksResponse] = await Promise.all([
        dashboardService.getPages(),
        dashboardService.getActivePackage(),
        dashboardService.getIncomingAdsTasks({ limit: 10 })
      ]);

      if (Array.isArray(pagesResponse) && pagesResponse.length > 0) {
        setPages(pagesResponse);
        setSelectedPage(pagesResponse[0]);
      } else if (pagesResponse && pagesResponse.success && pagesResponse.data && pagesResponse.data.pages) {
        setPages(pagesResponse.data.pages);
        if (pagesResponse.data.pages.length > 0) {
          setSelectedPage(pagesResponse.data.pages[0]);
        }
      }

      if (Array.isArray(packageResponse) && packageResponse.length > 0) {
        const pkg = packageResponse[0];
        const mappedPackage = {
          package_name: pkg.package,
          price: null,
          purchase_date: pkg.purchaseDate,
          activation_date: pkg.startDate,
          expiry_date: pkg.endDate,
          days_remaining: pkg.endDate ? Math.ceil((new Date(pkg.endDate) - new Date()) / (1000 * 60 * 60 * 24)) : 0,
          status: new Date(pkg.endDate) > new Date() ? 'active' : 'expired',
          status_text: new Date(pkg.endDate) > new Date() ? 'ใช้งานได้' : 'หมดอายุ',
          quota_type: pkg.quotaType,
          total_quota: pkg.totalQuota,
          used_quota: pkg.usedQuota,
          remaining_quota: pkg.remainingQuota
        };
        setActivePackage(mappedPackage);
      } else if (packageResponse && packageResponse.success && packageResponse.data && packageResponse.data.package) {
        setActivePackage(packageResponse.data.package);
      }

      if (Array.isArray(tasksResponse)) {
        const mappedTasks = tasksResponse.map(task => ({
          id: task._id,
          task_type: task.serviceType,
          task_type_text: task.serviceType?.toUpperCase(),
          task_type_text_th: getServiceTypeText(task.serviceType),
          scheduled_date: task.scheduledDate,
          page_name: task.pageName,
          budget: task.adsBudget,
          status: task.status,
          status_text: getStatusText(task.status),
          duration_hours: task.durationHours,
          special_requirements: task.specialRequirements,
          ads_user: task.adsUser,
          seller: task.seller
        }));
        setIncomingTasks(mappedTasks);
      } else if (tasksResponse.success && tasksResponse.data.tasks) {
        setIncomingTasks(tasksResponse.data.tasks);
      }

      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();

      const startOfMonth = moment().startOf('month').toISOString();
      const endOfMonth = moment().endOf('month').toISOString();

      const [calendarTasksResponse, weeklySummaryResponse] = await Promise.all([
        dashboardService.getAdsTasksByDateRange({
          startDate: startOfMonth,
          endDate: endOfMonth
        }),
        dashboardService.getAdsTasksSummaryByWeek({
          month: currentMonth,
          year: currentYear
        })
      ]);

      if (Array.isArray(calendarTasksResponse)) {
        const events = calendarTasksResponse.map(task => ({
          title: getServiceTypeText(task.serviceType),
          start: moment(task.scheduledDate).format("YYYY-MM-DD"),
          color: getTaskColor(task.serviceType)
        }));
        setCalendarEvents(events);
      } else if (calendarTasksResponse.success && calendarTasksResponse.data.tasks) {
        const events = calendarTasksResponse.data.tasks.map(task => ({
          title: task.task_type_text_th || task.task_type_text,
          start: moment(task.scheduled_date).format("YYYY-MM-DD"),
          color: getTaskColor(task.task_type)
        }));
        setCalendarEvents(events);
      }

      if (Array.isArray(weeklySummaryResponse)) {
        const weekNumbers = [...new Set(weeklySummaryResponse.map(item => item.week))].sort((a, b) => a - b);
        
        const stats = {
          weeks: weekNumbers.map(w => `สัปดาห์ ${w}`),
          boost_live: weekNumbers.map(weekNum => {
            const item = weeklySummaryResponse.find(r => r.week === weekNum && r.serviceType === 'live');
            return item?.count || 0;
          }),
          boost_post: weekNumbers.map(weekNum => {
            const item = weeklySummaryResponse.find(r => r.week === weekNum && r.serviceType === 'post');
            return item?.count || 0;
          }),
          consult: weekNumbers.map(weekNum => {
            const item = weeklySummaryResponse.find(r => r.week === weekNum && r.serviceType === 'consult');
            return item?.count || 0;
          })
        };
        setWeeklyStats(stats);
      } else if (weeklySummaryResponse.success && weeklySummaryResponse.data.weeks) {
        const weeks = weeklySummaryResponse.data.weeks;
        const stats = {
          weeks: weeks.map((w, idx) => `สัปดาห์ ${w.week_number}`),
          boost_live: weeks.map(w => w.tasks.boost_live?.count || 0),
          boost_post: weeks.map(w => w.tasks.boost_post?.count || 0),
          consult: weeks.map(w => w.tasks.consult?.count || 0)
        };
        setWeeklyStats(stats);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
      setAlertVariant("danger");
      setAlertMessage(err.response?.data?.error?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const getTaskColor = (taskType) => {
    const colors = {
      boost_live: '#dc3545',
      boost_post: '#28a745',
      consult: '#ffc107'
    };
    return colors[taskType] || '#50b5ff';
  };

  const getTaskTypeBadgeColor = (taskType) => {
    const colors = {
      boost_live: 'danger',
      boost_post: 'success',
      consult: 'warning',
      live: 'danger',
      post: 'success'
    };
    return colors[taskType] || 'primary';
  };

  const getServiceTypeText = (serviceType) => {
    const texts = {
      live: 'LIVE',
      post: 'POST',
      consult: 'CONSULT',
      boost_live: 'LIVE',
      boost_post: 'POST'
    };
    return texts[serviceType] || serviceType?.toUpperCase();
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'รอดำเนินการ',
      wait_assign: 'รอมอบหมายงาน',
      assigned: 'มอบหมายแล้ว',
      in_progress: 'กำลังดำเนินการ',
      completed: 'เสร็จสิ้น',
      cancelled: 'ยกเลิก'
    };
    return texts[status] || status;
  };

  const formatDateTime = (isoString) => {
    return moment(isoString).format('YYYY-MM-DD HH:mm');
  };

  const handleDateChange = async (dateInfo) => {
    try {
      const viewDate = dateInfo.view.currentStart;
      const startOfMonth = moment(viewDate).startOf('month').toISOString();
      const endOfMonth = moment(viewDate).endOf('month').toISOString();

      const calendarTasksResponse = await dashboardService.getAdsTasksByDateRange({
        startDate: startOfMonth,
        endDate: endOfMonth
      });

      if (Array.isArray(calendarTasksResponse)) {
        const events = calendarTasksResponse.map(task => ({
          title: getServiceTypeText(task.serviceType),
          start: moment(task.scheduledDate).format("YYYY-MM-DD"),
          color: getTaskColor(task.serviceType)
        }));
        setCalendarEvents(events);
      } else if (calendarTasksResponse.success && calendarTasksResponse.data.tasks) {
        const events = calendarTasksResponse.data.tasks.map(task => ({
          title: task.task_type_text_th || task.task_type_text,
          start: moment(task.scheduled_date).format("YYYY-MM-DD"),
          color: getTaskColor(task.task_type)
        }));
        setCalendarEvents(events);
      }
    } catch (err) {
      console.error('Error fetching calendar tasks:', err);
    }
  };

  const getServiceBadges = (services) => {
    if (!services) return [];
    const badges = [];
    if (services.boost_live?.total > 0) badges.push('LIVE');
    if (services.boost_post?.total > 0) badges.push('POST');
    if (services.consult?.total > 0) badges.push('CONSULT');
    return badges;
  };

  const getTotalRemaining = (services) => {
    if (!services) return 0;
    return (services.boost_live?.remaining || 0) +
           (services.boost_post?.remaining || 0) +
           (services.consult?.remaining || 0);
  };

  if (loading) {
    return (
      <div id="content-page" className="content-inner">
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">กำลังโหลดข้อมูล...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div id="content-page" className="content-inner">
        <Container>
          <Row className="row-eq-height">
            <Col md="12">
              <Row className="mb-5">
                <Col md="12">
                  <div className="card-header d-flex justify-content-between">
                    <div className="header-title">
                      <h4 className="card-title">แดชบอร์ด</h4>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {showAlert && (
                <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}
              
              {/* Page Info Section */}
              <Row>
                <Col md="6">
                  <Card className="mb-4 h-100">
                    <Card.Header>
                      <h5 className="card-title mb-0">ข้อมูลเพจ</h5>
                    </Card.Header>
                    <Card.Body className="py-3">
                      {selectedPage ? (
                        <div>
                          <h5 className="mb-2">{selectedPage.pageName}</h5>
                          <p className="text-muted mb-2 small">
                            <strong>หมวดหมู่:</strong> {selectedPage.category || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>ชื่อบัญชี:</strong> {selectedPage.accountName || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>ลูกค้า:</strong> {selectedPage.customerName || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>เบอร์โทร:</strong> {selectedPage.telephone || 'ไม่ระบุ'}
                          </p>
                          {selectedPage.pageLink && (
                            <p className="mb-2">
                              <a href={selectedPage.pageLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                ดูเพจ Facebook
                              </a>
                            </p>
                          )}
                          <div className="mt-2">
                            <Badge bg={selectedPage.activeStatus ? "success" : "secondary"} className="me-2">
                              {selectedPage.activeStatus ? 'ใช้งานอยู่' : 'ไม่ได้ใช้งาน'}
                            </Badge>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted mb-3">ยังไม่มีเพจที่เชื่อมต่อ</p>
                          <Button variant="primary" size="sm">
                            เชื่อมต่อเพจ Facebook
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md="6">
                  <Card className="mb-4 h-100">
                    <Card.Header>
                      <h5 className="card-title mb-0">แพ็คเกจปัจจุบัน</h5>
                    </Card.Header>
                    <Card.Body className="py-3">
                      {activePackage ? (
                        <>
                          <h5 className="text-primary mb-3">{activePackage.package_name}</h5>
                          <div className="mb-3">
                            <Badge 
                              bg={activePackage.status === 'active' ? 'success' : 'danger'} 
                              className="me-2 fs-5 px-3 py-2"
                            >
                              {activePackage.status === 'active' ? 'ใช้งานอยู่' : 'หมดอายุ'}
                            </Badge>
                            {activePackage.quota_type && (
                              <Badge bg="info" className="me-2">
                                {activePackage.quota_type === 'monthly' ? 'รายเดือน' : 'จำนวนครั้ง'}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>โควต้าทั้งหมด:</strong>
                              <span className="text-muted">{activePackage.total_quota || 'ไม่จำกัด'}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>ใช้ไปแล้ว:</strong>
                              <span className="text-muted">{activePackage.used_quota || 0} ครั้ง</span>
                            </div>
                            {activePackage.total_quota !== null && activePackage.total_quota !== undefined && activePackage.total_quota !== 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>คงเหลือ:</strong>
                                <Badge bg={activePackage.remaining_quota > 0 ? 'success' : 'danger'} className="fs-6">
                                  {activePackage.remaining_quota || 0} ครั้ง
                                </Badge>
                              </div>
                            )}
                          </div>
                          
                          <hr />
                          
                          <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>วันที่เริ่มต้น:</strong>
                              <span className="text-muted">
                                {activePackage.activation_date ? moment(activePackage.activation_date).format('DD/MM/YYYY') : '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>วันหมดอายุ:</strong>
                              <span className="text-muted">
                                {activePackage.expiry_date ? moment(activePackage.expiry_date).format('DD/MM/YYYY') : '-'}
                              </span>
                            </div>
                            {activePackage.days_remaining !== null && (
                              <div className="d-flex justify-content-between align-items-center">
                                <strong>เหลือเวลา:</strong>
                                {activePackage.status === 'active' ? (
                                  <Badge bg={activePackage.days_remaining > 7 ? 'success' : 'warning'}>
                                    {activePackage.days_remaining} วัน
                                  </Badge>
                                ) : (
                                  <Badge bg="danger">
                                    หมดอายุ
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {activePackage.purchase_date && (
                            <div className="mt-3">
                              <small className="text-muted">
                                วันที่ซื้อ: {moment(activePackage.purchase_date).format('DD/MM/YYYY HH:mm')}
                              </small>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted mb-3">ไม่มีแพ็คเกจที่ใช้งานอยู่</p>
                          <Link to="/portal/packages">
                            <Button variant="primary" size="sm">
                              ซื้อแพ็คเกจ
                            </Button>
                          </Link>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {/* Upcoming Queue Section */}
              <Row className="mt-4 mb-4">
                <Col md="6">
                  <Card className="mb-4 h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">คิวที่กำลังจะมาถึงล่าสุด</h5>
                      <FaCalendarCheck className="text-success" />
                    </Card.Header>
                    <div className="d-flex flex-column" style={{ minHeight: '450px' }}>
                      <ListGroup variant="flush" className="flex-grow-1">
                        {incomingTasks.length > 0 ? (
                          incomingTasks.map(task => (
                            <ListGroup.Item key={task.id}>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-1">
                                    <Badge bg={getTaskTypeBadgeColor(task.task_type)} className="me-2">
                                      {task.task_type_text_th || task.task_type_text}
                                    </Badge>
                                  </h6>
                                  <small className="text-muted d-block">
                                    {formatDateTime(task.scheduled_date)}
                                  </small>
                                  {task.page_name && (
                                    <small className="text-muted d-block">
                                      เพจ: {task.page_name}
                                    </small>
                                  )}
                                  {task.budget && (
                                    <small className="text-muted d-block">
                                      งบประมาณ: ฿{task.budget?.toLocaleString()}
                                    </small>
                                  )}
                                </div>
                                <Badge bg="info">{task.status_text}</Badge>
                              </div>
                            </ListGroup.Item>
                          ))
                        ) : (
                          <ListGroup.Item className="text-center py-4">
                            <p className="mb-0">ไม่มีคิวที่กำลังจะถึง</p>
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                      <Card.Footer className="text-center mt-auto">
                        <Link to="/portal/bookingHistory">
                          <Button variant="outline-primary" size="sm">
                            <FaHistory className="me-2" />
                            ดูประวัติคิวทั้งหมด
                          </Button>
                        </Link>
                      </Card.Footer>
                    </div>
                  </Card>
                </Col>
                
                {/* Calendar Section */}
                <Col md="6">
                  <Card className="mb-4 h-100">
                    <Card.Header>
                      <h5 className="card-title mb-0">ปฏิทินประจำเดือน</h5>
                    </Card.Header>
                    <Card.Body className="p-2 d-flex flex-column" style={{ minHeight: '450px' }}>
                      <FullCalendar
                        initialView="dayGridMonth"
                        height="auto"
                        plugins={[dayGridPlugin, bootstrapPlugin]}
                        headerToolbar={{
                          left: "prev,next",
                          center: "title",
                          right: ""
                        }}
                        buttonText={{
                          prev: '‹',
                          next: '›'
                        }}
                        events={calendarEvents}
                        dayMaxEventRows={false}
                        fixedWeekCount={false}
                        datesSet={handleDateChange}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {/* Service Statistics Chart */}
              <Row>
                <Col md="12">
                  <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">สถิติการใช้บริการคิว</h5>
                      <FaChartBar className="text-primary" />
                    </Card.Header>
                    <Card.Body>
                      <Chart
                        options={{
                          chart: {
                            type: 'bar',
                            height: 350,
                            stacked: false,
                            toolbar: {
                              show: false
                            }
                          },
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              columnWidth: '55%',
                              endingShape: 'rounded'
                            },
                          },
                          dataLabels: {
                            enabled: false
                          },
                          stroke: {
                            show: true,
                            width: 2,
                            colors: ['transparent']
                          },
                          xaxis: {
                            categories: weeklyStats.weeks,
                            title: {
                              text: 'รายสัปดาห์'
                            }
                          },
                          yaxis: {
                            title: {
                              text: 'จำนวนครั้งที่ใช้บริการ'
                            }
                          },
                          fill: {
                            opacity: 1
                          },
                          tooltip: {
                            y: {
                              formatter: function (val) {
                                return val + " ครั้ง"
                              }
                            }
                          },
                          legend: {
                            position: 'top',
                            horizontalAlign: 'center',
                            floating: false,
                            offsetY: 0,
                            offsetX: 0
                          }
                        }}
                        series={[
                          {
                            name: 'LIVE',
                            data: weeklyStats.boost_live,
                            color: '#008FFB'
                          },
                          {
                            name: 'POST',
                            data: weeklyStats.boost_post,
                            color: '#00E396'
                          },
                          {
                            name: 'CONSULT',
                            data: weeklyStats.consult,
                            color: '#FEB019'
                          }
                        ]}
                        type="bar"
                        height={350}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ClientDashboard;
